import React, { useMemo, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useCatalog } from "@/state/CatalogContext";
import { useConfig } from "@/lib/useConfig";
import { useCategories, inCategory } from "@/lib/categories";
import { mediaUrl } from "@/lib/api";
import { DI, DProductCard, DSkeletonCard, GROUP_ACCESSORIES, GROUP_EQUIPMENT, GROUP_KINDS, inSlug, useDesktopNav } from "@/components/desktop/desktopUi";
import type { FlavorNote, Locale, Product } from "@/types";

type Sort = "popular" | "priceAsc" | "priceDesc" | "rating";
type Roast = "light" | "medium" | "dark";
const roastOf = (p: Product): Roast | null => {
  const r = p.flavor?.roastLevel;
  if (!r) return null;
  return r <= 2 ? "light" : r === 3 ? "medium" : "dark";
};
/** "Обработка" comes from the product's characteristics, if the admin filled it in. */
const processOf = (p: Product): string | null => {
  const a = p.attributes ?? {};
  const key = Object.keys(a).find((k) => /обработ|process|ishlov/i.test(k));
  return key ? a[key].trim() || null : null;
};
const price = (p: Product) => p.variants[0]?.priceUzs ?? 0;

/** "Найдено 24 товара" with Russian plural forms. */
function foundText(n: number, locale: Locale, t: (k: string, v?: Record<string, string | number>) => string) {
  if (locale !== "ru") return t("desktop.found", { count: n });
  const m10 = n % 10, m100 = n % 100;
  const f = m10 === 1 && m100 !== 11 ? "one" : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14) ? "few" : "many";
  return t(`desktop.found_${f}`, { count: n });
}

function Check({ on, label, count, onClick }: { on: boolean; label: string; count?: number; onClick: () => void }) {
  return (
    <button className="dc-check" data-on={on} onClick={onClick}>
      <span className="dc-check__box" />
      <span className="dc-check__label">{label}</span>
      {count !== undefined && <em>{count}</em>}
    </button>
  );
}

/** Desktop catalogue — reference layout: banner, category pills, filters on the left, product grid. */
export function DesktopCatalog() {
  const { t, locale } = useI18n();
  const { route, navigate } = useRouter();
  const { products, loading } = useCatalog();
  const { site } = useConfig();
  const categories = useCategories();
  const slug = route.name === "catalog" ? route.category : undefined;
  const q = route.name === "catalog" ? (route.q ?? "").trim().toLowerCase() : "";
  const nav = useDesktopNav(categories);
  const group = slug === GROUP_EQUIPMENT ? nav.equipment : slug === GROUP_ACCESSORIES ? nav.accessories : null;
  const groupOf = (s?: string) => [nav.equipment, nav.accessories].find((g) => g?.categories.some((c) => c.slug === s)) ?? null;
  const activeGroup = group ?? groupOf(slug);
  // A whole group (Equipment / Accessories) or one category.
  const current: { name: string; kind: string; slug: string } | undefined = group
    ? { name: slug === GROUP_EQUIPMENT ? t("desktop.equipment") : t("desktop.accessories"), kind: GROUP_KINDS[slug!][0], slug: slug! }
    : categories?.find((c) => c.slug === slug);

  const [roasts, setRoasts] = useState<Roast[]>([]);
  const [processes, setProcesses] = useState<string[]>([]);
  const [notes, setNotes] = useState<FlavorNote[]>([]);
  const [minP, setMinP] = useState("");
  const [maxP, setMaxP] = useState("");
  const [inStock, setInStock] = useState(false);
  const [sort, setSort] = useState<Sort>("popular");
  const toggle = <T,>(list: T[], v: T, set: (x: T[]) => void) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const reset = () => { setRoasts([]); setProcesses([]); setNotes([]); setMinP(""); setMaxP(""); setInStock(false); };

  // Products of the chosen category (+ header search), before the side filters.
  const base = useMemo(() => {
    let list = slug ? products.filter((p) => inSlug(p, slug, inCategory)) : products;
    if (q) list = list.filter((p) => `${p.name} ${p.shortDescription} ${p.brand ?? ""}`.toLowerCase().includes(q));
    return list;
  }, [products, slug, q]);

  const roastCounts = useMemo(() => { const c: Record<string, number> = {}; base.forEach((p) => { const r = roastOf(p); if (r) c[r] = (c[r] ?? 0) + 1; }); return c; }, [base]);
  const processCounts = useMemo(() => { const c: Record<string, number> = {}; base.forEach((p) => { const r = processOf(p); if (r) c[r] = (c[r] ?? 0) + 1; }); return c; }, [base]);
  const noteCounts = useMemo(() => { const c: Partial<Record<FlavorNote, number>> = {}; base.forEach((p) => (p.flavor?.notes ?? []).forEach((n) => (c[n] = (c[n] ?? 0) + 1))); return c; }, [base]);

  const shown = useMemo(() => {
    const lo = Number(minP.replace(/\D/g, "")) || 0;
    const hi = Number(maxP.replace(/\D/g, "")) || Infinity;
    let list = base.filter((p) => {
      if (roasts.length && !roasts.includes(roastOf(p) as Roast)) return false;
      if (processes.length && !processes.includes(processOf(p) ?? "")) return false;
      if (notes.length && !notes.some((n) => p.flavor?.notes?.includes(n))) return false;
      if (inStock && !(p.orderMode === "buy_now" && (p.variants[0]?.stock ?? 0) > 0)) return false;
      const pr = price(p);
      if ((lo || hi !== Infinity) && (pr < lo || pr > hi)) return false;
      return true;
    });
    if (sort === "priceAsc") list = [...list].sort((a, b) => price(a) - price(b));
    if (sort === "priceDesc") list = [...list].sort((a, b) => price(b) - price(a));
    if (sort === "rating") list = [...list].sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
    return list;
  }, [base, roasts, processes, notes, minP, maxP, inStock, sort]);

  const hero = site?.catalogHeroImage || (current ? products.find((p) => inSlug(p, current.slug, inCategory))?.images[0] : products[0]?.images[0]);
  const coffeeMode = base.some((p) => p.category === "coffee");
  const filtersOn = roasts.length + processes.length + notes.length + (minP ? 1 : 0) + (maxP ? 1 : 0) + (inStock ? 1 : 0) > 0;

  return (
    <div className="dc">
      <section className="dc-hero">
        <span className="dh-hero__art" aria-hidden="true" />
        {hero && <div className="dc-hero__photo" style={{ backgroundImage: `url(${mediaUrl(hero)})` }} data-fallback={!site?.catalogHeroImage} />}
        <div className="dc-hero__text">
          <div className="dc-crumbs">
            <button onClick={() => navigate({ name: "home" })}>{t("desktop.crumbHome")}</button> ›{" "}
            <button onClick={() => navigate({ name: "catalog" })}>{t("desktop.catalog")}</button>
            {current && <> › <span>{current.name}</span></>}
          </div>
          <h1 className="dk-serif">{!current ? t("desktop.catalog") : current.kind === "coffee" ? t("desktop.catalogOf", { name: current.name.toLowerCase() }) : current.name}</h1>
          <p>{q ? t("desktop.searchResults", { q: route.name === "catalog" ? route.q ?? "" : "" }) : t(`desktop.catalogText.${current?.kind ?? "all"}`)}</p>
        </div>
      </section>

      <div className="dc-pills">
        <button data-active={!slug} onClick={() => navigate({ name: "catalog" })}>{t("desktop.allProducts")} ({products.length})</button>
        {nav.coffee.map((c) => {
          const n = products.filter((p) => inCategory(p, c.slug)).length;
          return n > 0 ? <button key={c.id} data-active={slug === c.slug} onClick={() => navigate({ name: "catalog", category: c.slug })}>{c.name} ({n})</button> : null;
        })}
        {[nav.equipment, nav.accessories].map((g) => {
          if (!g) return null;
          const n = products.filter((p) => inSlug(p, g.slug, inCategory)).length;
          return n > 0 ? (
            <button key={g.slug} data-active={activeGroup?.slug === g.slug} onClick={() => navigate({ name: "catalog", category: g.slug })}>
              {g.slug === GROUP_EQUIPMENT ? t("desktop.equipment") : t("desktop.accessories")} ({n})
            </button>
          ) : null;
        })}
      </div>
      {activeGroup && activeGroup.categories.length > 1 && (
        <div className="dc-subpills">
          <button data-active={slug === activeGroup.slug} onClick={() => navigate({ name: "catalog", category: activeGroup.slug })}>{t("desktop.allInGroup")}</button>
          {activeGroup.categories.map((c) => (
            <button key={c.id} data-active={slug === c.slug} onClick={() => navigate({ name: "catalog", category: c.slug })}>{c.name}</button>
          ))}
        </div>
      )}

      <div className="dc-layout">
        <aside className="dc-filters">
          {(coffeeMode || !slug) && (
            <button className="dc-fwcard" onClick={() => navigate({ name: "flavors" })}>
              <span className="dc-fwcard__wheel" aria-hidden="true" />
              <span>
                <b>{t("desktop.fwCardTitle")}</b>
                <small>{t("desktop.fwCardText")}</small>
              </span>
              {DI.arrow}
            </button>
          )}
          {coffeeMode && Object.keys(roastCounts).length > 0 && (
            <div className="dc-group">
              <h4 className="dk-serif">{t("desktop.fRoast")}</h4>
              {(["light", "medium", "dark"] as Roast[]).filter((r) => roastCounts[r]).map((r) => (
                <Check key={r} on={roasts.includes(r)} label={t(`desktop.roast.${r}`)} count={roastCounts[r]} onClick={() => toggle(roasts, r, setRoasts)} />
              ))}
            </div>
          )}
          {Object.keys(processCounts).length > 0 && (
            <div className="dc-group">
              <h4 className="dk-serif">{t("desktop.fProcess")}</h4>
              {Object.entries(processCounts).map(([k, n]) => <Check key={k} on={processes.includes(k)} label={k} count={n as number} onClick={() => toggle(processes, k, setProcesses)} />)}
            </div>
          )}
          {Object.keys(noteCounts).length > 0 && (
            <div className="dc-group">
              <h4 className="dk-serif">{t("desktop.fNotes")}</h4>
              {(Object.entries(noteCounts) as [FlavorNote, number][]).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                <Check key={k} on={notes.includes(k)} label={t(`flavor.notes.${k}`)} count={n} onClick={() => toggle(notes, k, setNotes)} />
              ))}
            </div>
          )}
          <div className="dc-group">
            <h4 className="dk-serif">{t("desktop.fPrice")}</h4>
            <div className="dc-price">
              <input inputMode="numeric" placeholder={t("desktop.from")} value={minP} onChange={(e) => setMinP(e.target.value.replace(/\D/g, ""))} />
              <span>—</span>
              <input inputMode="numeric" placeholder={t("desktop.to")} value={maxP} onChange={(e) => setMaxP(e.target.value.replace(/\D/g, ""))} />
            </div>
          </div>
          <div className="dc-group dc-group--last">
            <Check on={inStock} label={t("desktop.fInStock")} onClick={() => setInStock(!inStock)} />
          </div>
          {filtersOn && <button className="dc-reset" onClick={reset}>{t("desktop.resetFilters")}</button>}
        </aside>

        <div>
          <div className="dc-toolbar">
            <span className="dk-serif">{foundText(shown.length, locale, t)}</span>
            <label className="dc-sort">
              {t("desktop.sort")}:
              <select value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
                <option value="popular">{t("desktop.sortPopular")}</option>
                <option value="priceAsc">{t("desktop.sortPriceAsc")}</option>
                <option value="priceDesc">{t("desktop.sortPriceDesc")}</option>
                <option value="rating">{t("desktop.sortRating")}</option>
              </select>
            </label>
          </div>
          {loading && products.length === 0 ? (
            <div className="dc-grid" aria-busy="true">
              {Array.from({ length: 10 }, (_, i) => <DSkeletonCard key={i} imgHeight={210} />)}
            </div>
          ) : shown.length === 0 ? (
            <div className="dc-empty">
              {t("desktop.nothingFound")}
              {filtersOn && <button className="dk-btn dk-btn--ghost" onClick={reset}>{t("desktop.resetFilters")}</button>}
            </div>
          ) : (
            <div className="dc-grid">
              {shown.map((p) => <DProductCard key={p.id} product={p} imgHeight={210} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
